import dataService from "../../api";
import { startLoading, stopLoading, serverErrorMessage, showNotification, showAlert, catchHandler } from "../../utils/helpers";

// The organisation the system is delivered to (Settings > بيانات المؤسسة): its name, the body it
// belongs to, contact details and logo. Loaded once when the app starts (App.vue), before sign-in,
// and used by the login page, the app shell, printed documents and report prints.
//
// Fields (camelCase from Models/OrganizationProfile.OrganizationInfo):
//   nameAr, nameEn, parentNameAr, parentNameEn, addressAr, addressEn, phone, email, website,
//   hasLogo, logoVersion
const EMPTY = {
    nameAr: "",
    nameEn: null,
    parentNameAr: null,
    parentNameEn: null,
    addressAr: null,
    addressEn: null,
    phone: null,
    email: null,
    website: null,
    hasLogo: false,
    logoVersion: null,
};

// Shown where the organisation name goes until one is entered.
export const DEFAULT_SYSTEM_NAME = "نظام شؤون الموظفين";

const state = {
    org: { ...EMPTY },
    loaded: false,
};

const getters = {
    // The organisation name, or the generic system name while none is set.
    displayName: state => state.org.nameAr || DEFAULT_SYSTEM_NAME,
    // The logo URL with its version, so a new upload is not served from the browser cache; null without a logo.
    logoUrl: state => (state.org.hasLogo ? `/api/organization/logo?v=${state.org.logoVersion}` : null),
    // True when there is anything to print in a letterhead.
    hasLetterhead: state => !!(state.org.nameAr || state.org.hasLogo),
};

const mutations = {
    setOrg: (state, org) => {
        state.org = { ...EMPTY, ...(org || {}) };
        state.loaded = true;
    },
};

async function write(request) {
    try {
        startLoading();
        const response = await request();
        stopLoading();
        if (!response.data.statusCode) {
            showAlert(serverErrorMessage);
            return Promise.reject(new Error("statusCode"));
        }
        showNotification(response.data.message);
        return response.data;
    } catch (error) {
        stopLoading();
        if (error.message !== "statusCode") catchHandler(error);
        return Promise.reject(error);
    }
}

const actions = {
    // Silent: a failure leaves the defaults (no name, no logo) rather than an error on the login page.
    async load({ commit }) {
        try {
            const response = await dataService.organization.get();
            if (response.data.statusCode) commit("setOrg", response.data.organization);
        } catch (error) {
            commit("setOrg", null);
        }
    },
    async save({ commit }, payload) {
        const data = await write(() => dataService.organization.update(payload));
        commit("setOrg", data.organization);
        return data.organization;
    },
    async uploadLogo({ commit }, file) {
        const formData = new FormData();
        formData.append("file", file);
        const data = await write(() => dataService.organization.uploadLogo(formData));
        commit("setOrg", data.organization);
        return data.organization;
    },
    async deleteLogo({ commit }) {
        const data = await write(() => dataService.organization.deleteLogo());
        commit("setOrg", data.organization);
        return data.organization;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
